'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, RefreshCw } from 'lucide-react';

interface ProductFormData {
  name: string;
  category: string;
  barcode: string;
  wholesale_price: number;
  retail_price: number;
  tax_rate: number;
  unit_type: string;
  has_active_discount: boolean;
  discount_pct: number;
  storage_condition: string;
  image_url: string;
}

interface ProductFormProps {
  initialData?: Partial<ProductFormData & { name: string }>;
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
}

// ─── EAN-13 generator (Morocco prefix 619) ───────────────────────────────────
function generateEAN13(): string {
  const prefix = '619';
  let body = '';
  for (let i = 0; i < 9; i++) body += Math.floor(Math.random() * 10).toString();
  const digits = (prefix + body).split('').map(Number);
  let sum = 0;
  digits.forEach((d, i) => { sum += i % 2 === 0 ? d : d * 3; });
  const check = (10 - (sum % 10)) % 10;
  return prefix + body + check.toString();
}

// ─── Inline SVG barcode renderer ─────────────────────────────────────────────
function BarcodePreview({ value }: { value: string }) {
  const L = ['0001101','0011001','0010011','0111101','0100011','0110001','0101111','0111011','0110111','0001011'];
  const G = ['0100111','0110011','0011011','0100001','0011101','0111001','0000101','0010001','0001001','0010111'];
  const R = ['1110010','1100110','1101100','1000010','1011100','1001110','1010000','1000100','1001000','1110100'];
  const parityTable = ['LLLLLL','LLGLGG','LLGGLG','LLGGGL','LGLLGG','LGGLLG','LGGGLL','LGLGLG','LGLGGL','LGGLGL'];

  if (value.length !== 13) {
    return <p className="text-xs text-muted-foreground font-mono text-center">{value || '—'}</p>;
  }

  const digits = value.split('').map(Number);
  const parity = parityTable[digits[0]];
  let bars = '101';
  for (let i = 0; i < 6; i++) bars += parity[i] === 'L' ? L[digits[i + 1]] : G[digits[i + 1]];
  bars += '01010';
  for (let i = 7; i < 13; i++) bars += R[digits[i]];
  bars += '101';

  const width = 200, height = 48;
  const barWidth = width / bars.length;
  const rects: React.ReactNode[] = [];
  bars.split('').forEach((b, i) => {
    if (b === '1') rects.push(<rect key={i} x={i * barWidth} y={0} width={barWidth} height={height} fill="#111" />);
  });

  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        {rects}
      </svg>
      <p className="text-xs font-mono text-muted-foreground tracking-widest">{value}</p>
    </div>
  );
}

export default function ProductForm({ initialData, onSubmit, onCancel }: ProductFormProps) {
  const [loading, setLoading] = useState(false);
  const isNew = !initialData?.name;

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ProductFormData>({
    defaultValues: {
      name: initialData?.name || '',
      category: (initialData as any)?.category || 'Dairy',
      barcode: (initialData as any)?.barcode || (isNew ? generateEAN13() : ''),
      wholesale_price: (initialData as any)?.wholesale_price || 0,
      retail_price: (initialData as any)?.retail_price || 0,
      tax_rate: (initialData as any)?.tax_rate || 0.10,
      unit_type: (initialData as any)?.unit_type || 'Piece',
      has_active_discount: (initialData as any)?.has_active_discount || false,
      discount_pct: (initialData as any)?.discount_pct || 0,
      storage_condition: 'Dry',
      image_url: (initialData as any)?.image || '',
    },
  });

  const hasDiscount = watch('has_active_discount');
  const barcodeValue = watch('barcode');

  const regenerateBarcode = () => {
    setValue('barcode', generateEAN13(), { shouldValidate: true });
  };

  const handleFormSubmit = async (data: ProductFormData) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 800));
    setLoading(false);
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="p-5 space-y-5">
      {/* Section 1: Identification */}
      <div>
        <p className="section-header mb-3">Identification du Produit</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-600 text-foreground mb-1">Nom du Produit *</label>
            <p className="text-2xs text-muted-foreground mb-1.5">Nom complet tel qu'il apparaît sur l'étiquette</p>
            <input {...register('name', { required: 'Le nom du produit est requis' })} className="input-base" placeholder="ex: Fromage Frais Kiri 500g" />
            {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>

          {/* Barcode field with auto-generate */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-600 text-foreground mb-1">Code-Barres EAN-13 *</label>
            <p className="text-2xs text-muted-foreground mb-1.5">
              {isNew ? 'Généré automatiquement (préfixe Maroc 619) — modifiable ou régénérable' : 'Code EAN-13 pour scanner POS'}
            </p>
            <div className="flex gap-2 items-center">
              <input
                {...register('barcode', { required: 'Code-barres requis', minLength: { value: 8, message: 'Code trop court' } })}
                className="input-base font-mono flex-1"
                placeholder="6191000000000"
              />
              {isNew && (
                <button
                  type="button"
                  onClick={regenerateBarcode}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border text-xs font-600 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors whitespace-nowrap"
                  title="Générer un nouveau code-barres EAN-13"
                >
                  <RefreshCw size={13} />
                  Régénérer
                </button>
              )}
            </div>
            {errors.barcode && <p className="text-xs text-danger mt-1">{errors.barcode.message}</p>}

            {/* Live barcode preview */}
            {barcodeValue && barcodeValue.length >= 8 && (
              <div className="mt-3 p-3 bg-muted/50 border border-border rounded-xl flex flex-col items-center gap-1">
                <p className="text-2xs text-muted-foreground mb-1">Aperçu du code-barres</p>
                <BarcodePreview value={barcodeValue} />
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Catégorie *</label>
            <select {...register('category')} className="input-base">
              <option value="Dairy">Produits Laitiers</option>
              <option value="Vegetables">Légumes</option>
              <option value="Meats">Viandes</option>
              <option value="Pizzas">Pizzas</option>
            </select>
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Section 2: Pricing */}
      <div>
        <p className="section-header mb-3">Tarification & Taxes</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Prix Gros (MAD) *</label>
            <input type="number" step="0.01" {...register('wholesale_price', { required: true, min: 0 })} className="input-base font-tabular" placeholder="0.00" />
            {errors.wholesale_price && <p className="text-xs text-danger mt-1">Requis</p>}
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Prix Détail (MAD) *</label>
            <input type="number" step="0.01" {...register('retail_price', { required: true, min: 0 })} className="input-base font-tabular" placeholder="0.00" />
            {errors.retail_price && <p className="text-xs text-danger mt-1">Requis</p>}
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Taux TVA *</label>
            <select {...register('tax_rate')} className="input-base">
              <option value={0}>0% — Exonéré</option>
              <option value={0.07}>7% — Produits de base</option>
              <option value={0.10}>10% — Taux réduit</option>
              <option value={0.20}>20% — Taux normal</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Unité de Vente</label>
            <select {...register('unit_type')} className="input-base">
              <option value="Piece">Pièce</option>
              <option value="Kilogram">Kilogramme</option>
              <option value="Carton_Box">Carton</option>
            </select>
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Section 3: Discount */}
      <div>
        <p className="section-header mb-3">Remise Active</p>
        <div className="flex items-center gap-3 mb-3">
          <input type="checkbox" id="has_discount" {...register('has_active_discount')} className="w-4 h-4 rounded accent-primary" />
          <label htmlFor="has_discount" className="text-sm font-600 text-foreground cursor-pointer">Activer une remise sur ce produit</label>
        </div>
        {hasDiscount && (
          <div className="flex items-center gap-3">
            <div>
              <label className="block text-xs font-600 text-foreground mb-1">Pourcentage de Remise (%)</label>
              <input type="number" min={1} max={80} {...register('discount_pct')} className="input-base w-32 font-tabular" placeholder="10" />
            </div>
            <p className="text-xs text-muted-foreground mt-4">Le prix remisé sera affiché en doré dans le catalogue.</p>
          </div>
        )}
      </div>

      <hr className="border-border" />

      {/* Section 4: Storage */}
      <div>
        <p className="section-header mb-3">Stockage & Image</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">Condition de Stockage</label>
            <select {...register('storage_condition')} className="input-base">
              <option value="Dry">Sec (Température ambiante)</option>
              <option value="Chilled">Réfrigéré (0–8°C)</option>
              <option value="Frozen">Congelé (-18°C)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">URL Image Produit</label>
            <p className="text-2xs text-muted-foreground mb-1.5">URL Supabase Storage ou lien externe</p>
            <input {...register('image_url')} className="input-base" placeholder="https://..." />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-3 justify-end pt-2 border-t border-border">
        <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
        <button type="submit" disabled={loading} className="btn-primary min-w-[120px] justify-center">
          {loading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Enregistrement...
            </>
          ) : (
            initialData?.name ? 'Mettre à Jour' : 'Ajouter Produit'
          )}
        </button>
      </div>
    </form>
  );
}